import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { TimelineList } from "./molecules/TimelineList";
import { PortalHeader } from "../../molecules/Header";
import { DrawerOverlay, DrawerContent, DrawerBody } from "./index.styles";
import { History } from "../../../assets/icons";
import { ButtonT1 } from "../../atoms/Button";
import { IconWithNameForButton } from "../../atoms/Button/Button";
import { useDetailPageContext } from "../Provider/DetailV2";
const rootModals = document.getElementById("root-modals");

/**
 * Slide-out drawer component for displaying an entity's activity log.
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open.
 * @param {Function} props.onClose - Callback to close the drawer.
 * @param {Array} props.logs - The history data logs to display.
 * @param {boolean} props.isLoading - Whether the logs are currently loading.
 * @param {boolean} props.isError - Whether fetching the logs failed.
 * @param {boolean} [props.hasMore] - Whether there are more logs to fetch.
 * @param {boolean} [props.isFetching] - Whether a fetch is currently in progress.
 * @param {Function} [props.onLoadMore] - Callback to fetch the next page of logs.
 * @return {JSX.Element|null}
 */
export const ActivityLogDrawer = ({
  isOpen,
  onClose,
  logs,
  isLoading,
  isError,
  hasMore = false,
  isFetching = false,
  onLoadMore = () => {},
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animateOpen, setAnimateOpen] = useState(false);
  const drawerBodyRef = useRef(null);
  const sentinelRef = useRef(null);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        setAnimateOpen(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setAnimateOpen(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          onLoadMore();
        }
      },
      { root: drawerBodyRef.current, threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetching, onLoadMore]);

  if (!shouldRender || !rootModals) return null;

  return createPortal(
    <DrawerOverlay $isOpen={animateOpen} onClick={onClose}>
      <DrawerContent onClick={e => e.stopPropagation()}>
        <PortalHeader title={"Activity Log"} onClose={onClose} />

        <DrawerBody ref={drawerBodyRef}>
          <TimelineList logs={logs} isLoading={isLoading && !logs.length} isError={isError} />
          {hasMore && <div ref={sentinelRef} style={{ height: 20 }} />}
        </DrawerBody>
      </DrawerContent>
    </DrawerOverlay>,
    rootModals,
  );
};

/**
 * Standardized trigger button for opening the Activity Log drawer.
 * Relies on `useDetailPageContext` to set the `currentAction`.
 *
 * @returns {JSX.Element}
 */
export const ActivityLogButton = () => {
  const { setCurrentAction } = useDetailPageContext();

  return (
    <ButtonT1
      title={<IconWithNameForButton title="Activity Log" icon={History} />}
      $variant="primaryLight"
      onClickCallback={() => setCurrentAction("activityLog")}
    />
  );
};
